import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDataSubscription } from './queryKeys';

type DataSubscriptionResponse = {
  code: number;
  message: string;
  requestId: UUID | undefined;
};

/**
 * Deletes a data subscription.
 *
 * @param {UUID} dataSubscriptionId - An uuid containing the data subscription details to be deleted.
 * @returns {Promise<DataSubscriptionResponse>} - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Subscription/operation/delete_data_subscription_data_subscription__id__delete
 */
export const deleteDataSubscription = async (dataSubscriptionId: UUID) => {
  const response = await vestigasApi
    .delete(
      ENDPOINT.DATA_SUBSCRIPTION.DELETE_DATA_SUBSCRIPTION(dataSubscriptionId),
    )
    .json<DataSubscriptionResponse>();

  return response;
};

/**
 * Custom hook for deleting a data subscription using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationDeleteDataSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dataSubscriptionId: UUID) =>
      deleteDataSubscription(dataSubscriptionId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeysDataSubscription.getAllDataSubscription(),
      });
    },
  });
};
